/**
 * LA CLASSE DEL CHIP, UNA SOLA, per le fonti e per i tag.
 *
 * Sta in un modulo suo perché <TagsBlock> e <SourcesBlock> stanno uno sotto l'altro in coda
 * all'articolo, e devono essere della stessa famiglia: due file di chip che differiscono di un
 * pixel di padding o di una tinta di bordo si leggono come due componenti scritti da due persone in
 * due momenti diversi. Copiata in due file divergerebbe alla prima modifica di uno dei due — che è
 * esattamente come ci è arrivata: i tag erano `bg-zinc-100 px-3 py-1.5 text-sm` scritti a mano
 * dentro <ArticleView>, le fonti un elenco verticale, e nessuno dei due sapeva dell'altro.
 *
 * CIÒ CHE DISTINGUE LE DUE FILE NON È LO STILE DEL CHIP: sono l'occhiello della sezione («Tag»,
 * «Fonti»), il `#` dei tag e il conteggio dei dati delle fonti. Il vestito è lo stesso di proposito.
 *
 * `py-1.5` e non `py-1`: sono link, e i tag sono navigazione vera (portano a /tag/…), quindi l'area
 * cliccabile conta. Restano sotto i 44px consigliati — un chip da 44px in una fila da 8 riporterebbe
 * il blocco all'altezza dell'elenco che la fila ha sostituito — ma 30px si prendono col pollice, 26 no.
 */
export const CHIP =
  'inline-flex items-baseline gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:border-accent/40 hover:text-accent-strong'

/**
 * La variante in tinta d'accento (per-tenant, come <PracticalBox>). Oggi la porta un chip solo: la
 * fonte istituzionale, che nell'elenco verticale era il badge «fonte istituzionale» scritto per
 * esteso. Il bordo è più marcato del chip neutro perché il fondo pieno, da solo, su uno schermo
 * poco contrastato sparisce.
 */
export const CHIP_ACCENT =
  'inline-flex items-baseline gap-1.5 rounded-full border border-accent/25 bg-accent-soft px-3 py-1.5 text-xs font-medium text-accent-strong transition-colors hover:border-accent/50'

/**
 * L'OCCHIELLO, IN DUE COLORI E UNA SOLA SCALA.
 *
 * Le quattro sezioni in coda all'articolo lo portano tutte: «Informazioni pratiche», «Tag»,
 * «Fonti», «A cura di». Ne avevano DUE scale diverse — `text-xs … tracking-widest` in tinta
 * d'accento nel riquadro pratico, `text-sm … tracking-wide` in grigio sulle due file di chip — e
 * bastava guardarle una sotto l'altra per vedere che «TAG» e «FONTI» pesavano più di «INFORMAZIONI
 * PRATICHE» pur essendo titoli dello stesso rango. Una sola scala, quindi; a variare resta il
 * colore, che dice se il titolo sta dentro un riquadro in tinta o su fondo bianco.
 */
const EYEBROW = 'text-xs font-semibold uppercase tracking-widest'
export const SECTION_EYEBROW = `${EYEBROW} text-zinc-500`
export const BOX_EYEBROW = `${EYEBROW} text-accent-strong`

/**
 * I DUE REGISTRI DELLA CODA, e non uno di più.
 *
 * `TAIL_BOX` è il blocco pieno: contenuto che si consulta e che ha bisogno di stare dentro qualcosa
 * (le informazioni pratiche, la firma dell'autore). La tinta è ricavata dall'accento per-tenant,
 * mai un colore fisso.
 *
 * `TAIL_RULE` è la sezione aperta: un filetto, un occhiello, una fila di chip (tag, fonti).
 *
 * ERANO TRE, ED È IL DIFETTO CHE QUESTA COPPIA CHIUDE: il riquadro pratico in tinta d'accento, le
 * due file di chip col filetto grigio, e la firma dell'autore in un TERZO linguaggio inventato per
 * l'occasione — riquadro grigio `bg-zinc-50/70` con bordo `zinc-100` — con dentro un cerchio in
 * `bg-accent-soft`, cioè il verde del riquadro pratico dentro una scatola grigia, a due sezioni di
 * distanza da quel riquadro. Tre linguaggi per quattro blocchi che si leggono di fila.
 *
 * IL RIMBALZO FRA I DUE REGISTRI NON È UN PROBLEMA, è la struttura: riquadro, chip, chip, riquadro.
 * A distinguere i due riquadri pensano l'occhiello e l'unica forma che nel resto della coda non
 * compare — il cerchio dell'avatar.
 *
 * `pt`/`p` COMBACIANO DI PROPOSITO (20px, 24 da sm): così il filetto della sezione aperta e il bordo
 * superiore del riquadro cadono alla stessa distanza dal loro occhiello, e i quattro blocchi hanno
 * un ritmo solo. Il margine NON sta qui: lo tiene il contenitore in <ArticleView> (`space-y-6`),
 * che è l'unico a sapere quali blocchi esistono davvero su questo articolo.
 */
export const TAIL_BOX = 'rounded-card border border-accent/15 bg-accent-soft p-5 text-sm sm:p-6'
export const TAIL_RULE = 'border-t border-zinc-100 pt-5 sm:pt-6'
